import { DateTime } from 'luxon';
import { Streak } from '../Types/Streak';
import { UserLootBox } from '../Types/UserLootBox';
import { getApiInstance } from './Api';
import { mapUserLootBox } from './RewardsApi';

export class StreakApi {
  private calculateStreakLength(
    startAtISO: string,
    lastExtendedAtISO: string,
  ): number {
    if (!startAtISO || !lastExtendedAtISO) {
      return 0;
    }

    const startAt = DateTime.fromISO(startAtISO)
      .setZone(window.env.REACT_APP_DEFAULT_TIMEZONE!)
      .startOf('day');
    const end = DateTime.fromISO(lastExtendedAtISO)
      .setZone(window.env.REACT_APP_DEFAULT_TIMEZONE!)
      .startOf('day');
    const today = DateTime.now()
      .setZone(window.env.REACT_APP_DEFAULT_TIMEZONE!)
      .startOf('day');

    if (today.diff(end, 'days').days > 1) {
      return 0;
    }

    // +1 because if the first activity was today, diff is 0, but streak is 1; tomorrow diff will be 1, and streak will be 2
    return end.diff(startAt, 'days').days + 1;
  }

  private mapStreak(rawStreak: {
    id: string;
    startAt: string;
    lastExtendedAt: string;
  }): Streak {
    return {
      ...rawStreak,
      startAt: DateTime.fromISO(rawStreak.startAt)
        .setZone(window.env.REACT_APP_DEFAULT_TIMEZONE!)
        .toJSDate(),
      lastExtendedAt: DateTime.fromISO(rawStreak.lastExtendedAt)
        .setZone(window.env.REACT_APP_DEFAULT_TIMEZONE!)
        .toJSDate(),
      currentStreakLength: this.calculateStreakLength(
        rawStreak.startAt,
        rawStreak.lastExtendedAt,
      ),
    };
  }

  async getLatestUserStreak(): Promise<Streak> {
    try {
      const response = await getApiInstance().get(`/streak`);
      return response.data ? this.mapStreak(response.data) : null;
    } catch (error) {
      console.error('Error fetching user streak:', error);
      throw error;
    }
  }

  async getUserStreakHistory(fromDate: Date, toDate?: Date): Promise<Streak[]> {
    try {
      const response = await getApiInstance().get(`/streak/history`, {
        params: {
          fromDate: fromDate.toISOString(),
          toDate: toDate?.toISOString(),
        },
      });
      return response.data.map((streak: any) => this.mapStreak(streak));
    } catch (error) {
      console.error('Error fetching user streak history:', error);
      throw error;
    }
  }

  async updateUserStreak(): Promise<{ streak: Streak; lootBox?: UserLootBox }> {
    try {
      const response = await getApiInstance().post(`/streak`);
      return {
        streak: this.mapStreak(response.data.streak),
        lootBox: response.data.lootBox
          ? mapUserLootBox(response.data.lootBox)
          : undefined,
      };
    } catch (error) {
      console.error('Error updating user streak:', error);
      throw error;
    }
  }
}
